import { cloneDeep, isArray, isString, merge, omit, pick, transform } from 'lodash'

import GenericStoreCoordinatorService from 'Services/genericStoreCoordinator/Service'
import GlobalEventBus from 'Services/GlobalEventBus'
import moment from 'moment'
import { toJS } from 'mobx'

const CALENDAR_API_ITEMS_PER_PAGE = 250

// TODO: Clean this up using multiple functions and variables
function pickRouteFields (routeFields, query, type) {
  return transform(
    routeFields[type],
    (result, val) => {
      if (isString(val) && query[val]) {
        const queryString = `${query[val]}`
        if (queryString.match(',')) {
          result[val] = queryString.split(',')
        } else {
          result[val] = query[val]
        }
      } else if (query[val.key]) {
        result[val.key] = val.transform(query[val.key])
      }
    },
    {}
  )
}

function transformObjectForUrl (object) {
  return transform(object, (result, val, key) => {
    if (isArray(val)) {
      result[key] = val.join(',')
    } else {
      result[key] = val
    }
  })
}

function pickSortField (query) {
  const splitStr = (query['sortOrder'] || '').split('|')
  const sortArray = []
  if (splitStr.length > 1) {
    sortArray.push({ sortField: splitStr[0], direction: splitStr[1] })
  }
  return sortArray
}

function pickMergeFields (query) {
  return {
    mode: query['mode'],
  }
}

function getRouteQuery (vm) {
  if (vm.$route && vm.$route.query) {
    return vm.$route.query
  }

  return Object.fromEntries(new URLSearchParams(window.location.search).entries())
}

function replaceUrlQuery (query) {
  const searchParams = new URLSearchParams()

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, value)
    }
  })

  const nextSearch = searchParams.toString()
  const nextUrl = nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname

  window.history.replaceState(window.history.state, '', nextUrl)
}

export default function ({
  type,
  defaultParams = {},
  calendar = false,
  routeFields = {}, // Fields that should be sent to API and included in the URL
  routeFieldsNoApi = {}, // Fields that should NOT be sent to API but should be included in the URL

  // TODO: Convert this to an object to match the other two routeField params
  omitRouteFields = [], // Fields that should be sent to the API but should NOT be included in the URL
  busSubscriptions = [],
  debug = false,
}) {
  if (type === undefined) {
    throw Error("GenericIndexMixinFactory requires 'type' to be defined.")
  }

  if (Array.isArray(routeFields)) {
    throw Error("GenericIndexMixinFactory requires 'routeFields' to be an Object, not an Array.")
  }

  return {
    props: {
      referenceNamePrefix: {
        type: String,
        default: undefined,
      },
    },
    fromMobx: {
      listCoordinatorFilter () {
        return this.listCoordinator.filterWithoutPage
      },
      currentPage () {
        return this.listCoordinator.currentPage
      },
      listCoordinatorLoading () {
        return this.listCoordinator.loadingData && this.listCoordinator.loadingData.any
      },
    },
    methods: {
      replaceRoute () {
        const sortOrder =
          this.listCoordinatorFilter.sortOrder.length > 0
            ? `${this.listCoordinatorFilter.sortOrder[0].sortField}|${
            this.listCoordinatorFilter.sortOrder[0].direction
            }`
            : undefined

        const routeWithFieldsOmitted = omit(toJS(this.listCoordinatorFilter), omitRouteFields)
        const routeQuery = getRouteQuery(this)
        const additionalRouteFields = pick(routeQuery, routeFieldsNoApi[type])
        // TODO: Potentially deprecate the routeFieldNoApi param in favor of this.localQueryParams
        const localQueryParams = this.localQueryParams || {}
        const nextQuery = transformObjectForUrl({
          ...routeWithFieldsOmitted,
          page: this.currentPage,
          sortOrder,
          ...additionalRouteFields,
          ...localQueryParams,
        })

        if (this.$router && this.$route) {
          this.$router.replace({
            name: this.$route.name,
            params: this.$route.params,
            meta: this.$route.meta,
            query: nextQuery,
          })
        } else {
          replaceUrlQuery(nextQuery)
        }
      },
      updateCoordinatorSortOrder (order) {
        this.listCoordinator.sortOrder = cloneDeep(order)
      },
    },
    data () {
      const slaveControlledFields = ['start_date', 'end_date', 'page']

      const returnData = {
        listCoordinator: GenericStoreCoordinatorService.get(type, {
          storeOpts: { autoFetch: false },
        }, this.referenceNamePrefix ? `${this.referenceNamePrefix}:list` : undefined),
        unsubscribeFromBusses: [],
      }

      returnData.listCoordinator.dataStore.debug = debug

      if (calendar) {
        returnData.calendarCoordinator = GenericStoreCoordinatorService.get(
          type,
          {
            storeOpts: { autoFetch: false, fetchAll: true },
            defaultFilter: {
              start_date: moment().startOf('month').startOf('week').format('MM/DD/YYYY'),
              end_date: moment().endOf('month').endOf('week').format('MM/DD/YYYY'),
            },
            slaveTo: {
              uuid: returnData.listCoordinator.uuid,
              slaveControlledFields,
              masterMergeFieldsTransform (m) {
                return {
                  ...omit(m, slaveControlledFields),
                  'per_page': CALENDAR_API_ITEMS_PER_PAGE,
                }
              },
            },
          },
          this.referenceNamePrefix ? `${this.referenceNamePrefix}:calendar` : undefined
        )
      }
      return returnData
    },
    created () {
      const routeQuery = getRouteQuery(this)

      this.listCoordinator.filter = merge(
        {},
        defaultParams,
        pickRouteFields(routeFields, routeQuery, type)
      )
      this.listCoordinator.sortOrder = pickSortField(routeQuery)
      this.listCoordinator.mergeFields = pickMergeFields(routeQuery)
      this.$watch('listCoordinatorFilter', (newVal, oldVal) => {
        if (newVal) {
          this.replaceRoute()
        }
      })
      this.$watch('currentPage', (newVal, oldVal) => {
        if (newVal) {
          this.replaceRoute()
        }
      })
      if (this.$route) {
        this.$watch('$route.query', (newVal, oldVal) => {
          const queryEmpty = Object.keys(newVal).length === 0

          if (queryEmpty) {
            this.listCoordinator.filter = merge({}, defaultParams)
          }
        })
      }

      busSubscriptions.forEach(sub => {
        this.unsubscribeFromBusses.push(GlobalEventBus.subscribe(sub, () => {
          this.listCoordinator.reloadDataStore()
          if (calendar) {
            this.calendarCoordinator.reloadDataStore()
          }
        }))
      })
    },
    beforeDestroy () {
      this.unsubscribeFromBusses.forEach(fn => { fn() })
    },
  }
}
